import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(searchQuery.trim())}`;
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md shadow-sm border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-8">
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-scales-3-line text-white text-sm md:text-base" />
              </div>
              <span className={`font-heading text-lg md:text-xl font-semibold tracking-tight transition-colors ${
                scrolled ? 'text-foreground-950' : 'text-foreground-950'
              }`}>
                ПРАВО
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {[
                { label: 'Каталог', href: '/#catalog' },
                { label: 'Судебная практика', href: '/courts/district' },
              ].map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-md transition-colors whitespace-nowrap"
                >
                  {item.label}
                </a>
              ))}
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-1 md:gap-2">
              {/* Search toggle */}
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors"
                aria-label="Поиск"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`ri-search-line text-lg ${scrolled ? 'text-foreground-700' : 'text-foreground-700'}`} />
                </div>
              </button>

              {/* Login */}
              <a
                href="/__auth/login"
                className="hidden md:flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line text-sm" />
                </div>
                Войти
              </a>

              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors"
                aria-label="Меню"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-lg text-foreground-700`} />
                </div>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Search overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-[60] bg-background-950/40 backdrop-blur-sm flex items-start justify-center pt-24 md:pt-32 px-4">
          <div className="w-full max-w-2xl bg-background-50 rounded-2xl shadow-xl border border-background-200/70 p-4 md:p-6">
            <form onSubmit={handleSearch} className="flex items-center gap-3">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-search-line text-foreground-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Поиск по 904,273 документам..."
                className="flex-1 bg-transparent text-foreground-950 placeholder:text-foreground-400 text-base md:text-lg outline-none"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-close-line text-foreground-500" />
                </div>
              </button>
            </form>
            <div className="mt-4 pt-4 border-t border-background-200/70">
              <p className="text-xs text-foreground-500 mb-2">Популярные запросы:</p>
              <div className="flex flex-wrap gap-2">
                {['Гражданский кодекс', 'Налоговый кодекс', 'Трудовой кодекс', 'Уголовный кодекс'].map((tag) => (
                  <button
                    key={tag}
                    onClick={() => {
                      setSearchQuery(tag);
                      window.location.href = `/search?q=${encodeURIComponent(tag)}`;
                      setSearchOpen(false);
                    }}
                    className="px-3 py-1.5 text-xs bg-background-100 hover:bg-background-200 text-foreground-700 rounded-full transition-colors whitespace-nowrap"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[55] md:hidden">
          <div className="absolute inset-0 bg-background-950/30" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute top-14 left-0 right-0 bg-background-50 border-b border-background-200/70 shadow-lg p-4">
            <nav className="flex flex-col gap-1">
              {[
                { label: 'Каталог', href: '/#catalog', icon: 'ri-book-3-line' },
                { label: 'Судебная практика', href: '/courts/district', icon: 'ri-auction-line' },
                { label: 'Войти', href: '/login', icon: 'ri-user-line' },
              ].map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={item.icon} />
                  </div>
                  <span className="font-medium">{item.label}</span>
                </a>
              ))}
            </nav>
          </div>
        </div>
      )}
    </>
  );
}